<template lang="pug">
div(
    :id="id"
    data-form-validator
    class="tl-input"
)
    div(
        :class="{ 'errorFile': error, disabled: disabled }"
        class="input-file"
    )
        p(
            v-if="label"
        ).input-file-label {{ label }}
        input(
            ref="inputFile"
            type="file"
            class="file"
            :accept="accept"
            @change="handleFiles"
        )

        transition(
            name="input-file-transition"
            mode="out-in"
        )
            tl-button(
                v-if="!model"
                class="button"
                small
                opaq
                :color="error ? 'error' : 'tl-gray'"
                :full-width="bp.tablet.value"
                :disabled="disabled"
                @click="triggerInputFile"
            ) {{ buttonLabel }}

            tl-position(
                v-else
                x="stretch"
                y="center"
                no-wrap
                class="full-width"
            )
                tl-position(
                    y="center"
                    no-wrap
                    @click="$emit('download-file', model)"
                )
                    i(
                        style="margin-right: 8px"
                        :style="{ color: disabled ? 'rgba(0,0,0,0.6)' : 'var(--color-tl-blue)' }"
                        class="mi-document"
                    )
                    span(
                        class="filename text-5"
                        :style="{ color: disabled ? 'rgba(0,0,0,0.6)' : 'var(--color-tl-blue)' }"
                    ) {{ model.name }}
                        template(v-if="showFileSize")
                            |  ({{ bytes(model.size) }})

                tl-button(
                    small
                    icon
                    outlined
                    color="gray"
                    :disabled="disabled"
                    style="flex: 0 0 30px"
                    @click="resetFile()"
                )
                    i.mi-delete

        tl-input-error-text(
            :error="error"
        )
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useBreakpoints } from '@/composables/ui/useBreakpoints'
import { useEventBus } from '@vueuse/core'
import prettyBytes from 'pretty-bytes'
import useRules from './validator/rules'
import uniqid from 'uniqid'

const props = withDefaults(defineProps<{
    label?: string
    buttonLabel?: string
    accept?: string
    error?: string | boolean
    disabled?: boolean
    rules?: string[]
    showFileSize?: boolean
}>(), {
    buttonLabel: 'Upload your file',
    accept: 'application/pdf',
    disabled: false,
    showFileSize: true,
})

const model = defineModel<File | null>({ default: null, required: true })

const emit = defineEmits(['resetError', 'download-file'])

const bp = useBreakpoints()
const { validateLogic } = useRules()

const id = ref<string>()
const inputFile = ref<HTMLInputElement>()
const internalError = ref<string | null>(null)

const error = computed(() => (props.error ? props.error : internalError.value))

const bytes = (val: number) => prettyBytes(val)

const triggerInputFile = () => inputFile.value?.click()

const handleFiles = (event: Event) => {
    const target = event.target as HTMLInputElement

    if (!target?.files) {
        return
    }

    const files = Array.from(target.files)

    model.value = files[0]

    emit('resetError')
    internalError.value = null
}
const resetFile = () => {
    if (props.disabled) return false

    model.value = null
    emit('resetError')
    internalError.value = null
    if (inputFile.value) {
        inputFile.value.value = ''
    }
}

onMounted(() => {
    id.value = uniqid()

    useEventBus('validate-' + id.value).on(() => {
        validateLogic(props, model, inputFile, internalError, emit)
    })

    useEventBus('clear-error-' + id.value).on(() => {
        resetFile()
    })
})
</script>

<style lang="sass" scoped>
.input-file
    padding: 10px
    border-radius: var(--border-radius)
    border: 1px solid var(--color-gray-tint-medium)
    background-color: var(--color-white)
    overflow: hidden

    .mi-document, .filename
        cursor: pointer

    &-label
        opacity: 0.7
        font-size: 12px
        font-weight: var(--font-weight-regular)
        margin-bottom: 8px

    &.errorFile
        background-color: var(--color-error-tint-light)

        .button
            animation: shake 0.52s cubic-bezier(.36,.07,.19,.97) both .32s
            background-color: var(--color-error-tint-light)

    .file
        display: none

    &.disabled
        outline: rgba(0, 0, 0, 0)
        border-color: rgba(0,0,0,0.0) !important
        color: rgba(0,0,0,0.5)
        background-color: rgba(0,0,0,0.1)

.input-file-transition-enter-active
    transition: all .2s ease-in-out
    transition-delay: .1s

.input-file-transition-enter
    opacity: 0
    transform: translateY(30px)

.input-file-transition-enter-to
    opacity: 1
    transform: translateY(0px)

.input-file-transition-leave-active
    transition: all .2s ease-in-out

.input-file-transition-leave
    opacity: 1
    transform: translateY(0px)

.input-file-transition-leave-to
    opacity: 0
    transform: translateY(-30px)
</style>
